import * as THREE from 'three';
import { AssetManager, ASSET_URLS } from './Assets';

import { ScenarioLogDef, TerrainDef, ScenarioDef } from '../types';

const TERRAIN_SIZE = 100;
const TERRAIN_SEGMENTS = 200;

export function createTerrainMaterial(): THREE.MeshStandardMaterial {
  const leavesTexture = AssetManager.getTexture(ASSET_URLS.TEXTURES.LEAVES);
  const mudTexture = AssetManager.getTexture(ASSET_URLS.TEXTURES.MUD);

  if (leavesTexture) {
    leavesTexture.wrapS = THREE.RepeatWrapping;
    leavesTexture.wrapT = THREE.RepeatWrapping;
    leavesTexture.repeat.set(20, 20);
  }
  if (mudTexture) {
    mudTexture.wrapS = THREE.RepeatWrapping;
    mudTexture.wrapT = THREE.RepeatWrapping;
    mudTexture.repeat.set(20, 20);
  }

  const groundMat = new THREE.MeshStandardMaterial({
    map: leavesTexture || null,
    color: leavesTexture ? 0xffffff : 0x555555,
    roughness: 0.9,
  });

  if (mudTexture) {
    groundMat.customProgramCacheKey = () => 'terrain_path';
    groundMat.onBeforeCompile = (shader) => {
      shader.uniforms.pathMap = { value: mudTexture };
      
      shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        `#include <common>
        attribute float pathIntensity;
        varying float vPathIntensity;`
      );
      shader.vertexShader = shader.vertexShader.replace(
        '#include <fog_vertex>',
        `#include <fog_vertex>
        vPathIntensity = pathIntensity;`
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        `#include <common>
        uniform sampler2D pathMap;
        varying float vPathIntensity;
        
        // Simple 2D pseudo-random noise for breaking up textures
        float random(vec2 st) {
            return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
        }
        
        float noise(vec2 st) {
            vec2 i = floor(st);
            vec2 f = fract(st);
            float a = random(i);
            float b = random(i + vec2(1.0, 0.0));
            float c = random(i + vec2(0.0, 1.0));
            float d = random(i + vec2(1.0, 1.0));
            vec2 u = f * f * (3.0 - 2.0 * f);
            return mix(a, b, u.x) + (c - a)* u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <map_fragment>',
        `
        #ifdef USE_MAP
          // Calculate a macro UV (independent of the 20x repeat)
          vec2 macroUv = vMapUv / 20.0; 
          
          // Break up the grid by sampling at two different scales/rotations
          // Second UV: rotated 45 degrees and scaled
          float sin45 = 0.7071;
          mat2 rot = mat2(sin45, -sin45, sin45, sin45);
          vec2 uv2 = rot * vMapUv * 0.6;
          
          vec4 baseColor1 = texture2D(map, vMapUv);
          vec4 baseColor2 = texture2D(map, uv2);
          
          // Blend using a macro noise map
          float n1 = noise(macroUv * 15.0); // Large noise pattern
          vec4 sampledDiffuseColor = mix(baseColor1, baseColor2, n1);
          
          // Do the same for the path texture
          vec4 pathColor1 = texture2D(pathMap, vMapUv);
          vec4 pathColor2 = texture2D(pathMap, uv2);
          vec4 pathColor = mix(pathColor1, pathColor2, n1);
          
          // Add a green tint to the non-trail area to look like vegetation
          vec4 vegetationTint = vec4(0.6, 1.0, 0.5, 1.0);
          vec4 tintedBaseColor = sampledDiffuseColor * vegetationTint;
          
          // Add noise to the path edge blending to make it natural
          float edgeNoise = noise(macroUv * 50.0) * 0.5 - 0.25;
          float noisyIntensity = clamp(vPathIntensity + edgeNoise, 0.0, 1.0);
          
          // Smooth the noisy intensity slightly
          noisyIntensity = smoothstep(0.1, 0.9, noisyIntensity);
          
          sampledDiffuseColor = mix(tintedBaseColor, pathColor, noisyIntensity);
          diffuseColor *= sampledDiffuseColor;
        #endif
        `
      );
    };
  }

  return groundMat;
}

export class Environment {
  private groundMesh: THREE.Mesh | null = null;
  private hazardMeshes: THREE.Object3D[] = [];

  constructor(private scene: THREE.Scene) {}

  public clear() {
    if (this.groundMesh) {
      this.scene.remove(this.groundMesh);
      if (this.groundMesh.geometry) {
        this.groundMesh.geometry.dispose();
      }
      if (this.groundMesh.material) {
        const materials = Array.isArray(this.groundMesh.material) ? this.groundMesh.material : [this.groundMesh.material];
        for (const mat of materials) {
          mat.dispose();
        }
      }
      this.groundMesh = null;
    }
    
    for (const mesh of this.hazardMeshes) {
      this.scene.remove(mesh);
    }
    this.hazardMeshes = [];
  }

  public generate(scenario: ScenarioDef): TerrainDef {
    const logs = scenario.logs;
    this.clear();
    const size = TERRAIN_SIZE;
    const segments = TERRAIN_SEGMENTS;
    // Ground Plane
    const groundGeo = new THREE.PlaneGeometry(size, size, segments, segments);
    
    // Rotate to be horizontal
    groundGeo.rotateX(-Math.PI / 2);

    // 1. Calculate Base Terrain Clearance
    let lowestLogBottom = 0;
    for (const log of logs) {
      const logBottom = log.position[1] - (log.diameter / 2);
      if (logBottom < lowestLogBottom) {
        lowestLogBottom = logBottom;
      }
    }
    const baseTerrainY = Math.min(0, lowestLogBottom - 1.0);

    // 2. Generate Heightmap
    const vertices = groundGeo.attributes.position.array as Float32Array;
    const heightmap = new Float32Array((segments + 1) * (segments + 1));
    const pathIntensities = new Float32Array((segments + 1) * (segments + 1));
    const halfSize = size / 2;
    const step = size / segments;

    let index = 0;
    for (let i = 0; i <= segments; i++) {
      const z = -halfSize + i * step;
      const pathX = Math.sin(z * 0.1) * 3.0 + Math.sin(z * 0.03) * 5.0;
      for (let j = 0; j <= segments; j++) {
        const x = -halfSize + j * step;
        
        let maxH = baseTerrainY;

        for (const log of logs) {
          if (!log.supports) continue;
          
          const lx = log.position[0];
          const lz = log.position[2];
          const radius = log.diameter / 2;
          
          const rotY = log.rotation[1] * Math.PI / 180;
          const cosY = Math.cos(-rotY);
          const sinY = Math.sin(-rotY);
          
          for (const support of log.supports) {
            if (support.type !== 'mound') continue;
            
            const dxWorld = x - lx;
            const dzWorld = z - lz;
            
            const localX = dxWorld * cosY - dzWorld * sinY;
            const localZ = dxWorld * sinY + dzWorld * cosY;
            
            const dX = localX - support.x;
            const dZ = localZ - support.z;
            
            const hX = Math.exp(- (dX * dX) / ((support.dx / 2) * (support.dx / 2)));
            
            // support.y is now treated as absolute world Y height in meters
            const peakHeight = support.y;
            const lipDepth = 0.15; 
            const falloff = support.dz / 2;
            
            let hZ = baseTerrainY;
            const absDZ = Math.abs(dZ);
            
            if (absDZ < radius) {
               hZ = peakHeight + lipDepth * (absDZ / radius) * (absDZ / radius);
            } else {
               const startH = peakHeight + lipDepth;
               const decay = Math.exp(- ((absDZ - radius) * (absDZ - radius)) / (falloff * falloff));
               hZ = baseTerrainY + (startH - baseTerrainY) * decay;
            }
            
            const moundH = baseTerrainY + (hZ - baseTerrainY) * hX;
            
            if (moundH > maxH) {
              maxH = moundH;
            }
          }
        }

        heightmap[index] = maxH;
        vertices[index * 3 + 1] = maxH;

        const distToPath = Math.abs(x - pathX);
        const pathWidth = 2.0;
        const fade = 4.0;
        let intensity = 0;
        if (distToPath < pathWidth) {
          intensity = 1.0;
        } else if (distToPath < pathWidth + fade) {
          intensity = 1.0 - (distToPath - pathWidth) / fade;
          intensity = intensity * intensity * (3.0 - 2.0 * intensity); // Smoothstep
        }
        pathIntensities[index] = intensity;

        index++;
      }
    }
    
    groundGeo.computeVertexNormals();
    groundGeo.setAttribute('pathIntensity', new THREE.BufferAttribute(pathIntensities, 1));

    const groundMat = createTerrainMaterial();
    const groundMesh = new THREE.Mesh(groundGeo, groundMat);
    groundMesh.receiveShadow = true;
    
    this.scene.add(groundMesh);
    this.groundMesh = groundMesh;
    
    if (scenario.ohlec?.hazards) {
      for (const hazard of scenario.ohlec.hazards) {
        let modelUrl: string | undefined;
        if (hazard.model) {
          const key = hazard.model.toUpperCase() as keyof typeof ASSET_URLS.MODELS;
          modelUrl = ASSET_URLS.MODELS[key];
        }

        if (modelUrl) {
          const modelData = AssetManager.getModel(modelUrl);
          if (modelData) {
            const mesh = modelData.scene.clone();
            mesh.position.set(hazard.position[0], hazard.position[1], hazard.position[2]);
            if (hazard.rotation) {
              mesh.rotation.set(
                hazard.rotation[0] * Math.PI / 180,
                hazard.rotation[1] * Math.PI / 180,
                hazard.rotation[2] * Math.PI / 180
              );
            }
            mesh.traverse((child) => {
              child.userData.hazardDef = hazard;
              child.userData.hazardRoot = mesh;
            });
            this.scene.add(mesh);
            this.hazardMeshes.push(mesh);
          } else {
            console.warn(`[Environment] Hazard model not found for URL: ${modelUrl}`);
          }
        }
      }
    }
    
    return {
      heightmap,
      size,
      segments
    };
  }

  public getGroundMesh(): THREE.Mesh | null {
    return this.groundMesh;
  }

  public getHazardMeshes(): THREE.Object3D[] {
    return this.hazardMeshes;
  }
}
